import * as Sentry from '@sentry/nextjs';

import type {
  OrderImportProcessResponse,
  OrderImportSourceColumns,
  ProcessedOrderImportLine,
} from '../../../client-queries';

interface ExportRow {
  skuId: string;
  quantity: string;
  description: string;
  myMixColorId: string;
  error: string;
}

const DANGEROUS_FORMULA_PREFIX = /^[=+\-@\t\r]/;

const sanitizeSpreadsheetCell = (value: string): string => {
  if (!value) {
    return '';
  }

  return DANGEROUS_FORMULA_PREFIX.test(value) ? `'${value}` : value;
};

export const getResultFileName = (rawFileName: string, now = new Date()): string => {
  const strippedFileName = rawFileName.replace(/\.[^.]+$/, '');
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

  return `${strippedFileName || 'order-import'}_result_${date}_${time}.xlsx`;
};

export const getSourceColumns = (
  result: OrderImportProcessResponse | null,
  t: (key: string) => string,
): OrderImportSourceColumns => {
  if (result?.sourceColumns) {
    return {
      skuId: result.sourceColumns.skuId || t('orderImport.upload.export.fallbackColumns.skuId'),
      quantity: result.sourceColumns.quantity || t('orderImport.upload.export.fallbackColumns.quantity'),
      description: result.sourceColumns.description || t('orderImport.upload.export.fallbackColumns.description'),
      myMixColorId: result.sourceColumns.myMixColorId || t('orderImport.upload.export.fallbackColumns.myMixColorId'),
    };
  }

  return {
    skuId: t('orderImport.upload.export.fallbackColumns.skuId'),
    quantity: t('orderImport.upload.export.fallbackColumns.quantity'),
    description: t('orderImport.upload.export.fallbackColumns.description'),
    myMixColorId: t('orderImport.upload.export.fallbackColumns.myMixColorId'),
  };
};

export const toExportRow = (
  row: ProcessedOrderImportLine,
  resolveErrorMessage: (errorKey: string) => string,
): ExportRow => {
  return {
    skuId: row.skuId || '',
    quantity: row.quantity !== undefined ? String(row.quantity) : '',
    description: row.description || '',
    myMixColorId: row.enrichment?.color?.data?.colorCode || row.myMixColorId || '',
    error: row.errors.length ? row.errors.map(resolveErrorMessage).join(' - ') : '',
  };
};

export const exportOrderImportResult = async (
  result: OrderImportProcessResponse,
  fileName: string,
  sourceColumns: OrderImportSourceColumns,
  errorColumnLabel: string,
  unprocessedSheetName: string,
  allRowsSheetName: string,
  resolveErrorMessage: (errorKey: string) => string,
): Promise<void> => {
  try {
    const xlsxModule = await import('xlsx');
    const XLSX = xlsxModule.default || xlsxModule;
    const workbook = XLSX.utils.book_new();
    const invalidRows = result.rows.filter((row: ProcessedOrderImportLine) => row.errors.length > 0);
    const allRows = result.rows;

    const buildSheetData = (rows: ProcessedOrderImportLine[]) => {
      return [
        [
          sanitizeSpreadsheetCell(sourceColumns.skuId),
          sanitizeSpreadsheetCell(sourceColumns.quantity),
          sanitizeSpreadsheetCell(sourceColumns.description),
          sanitizeSpreadsheetCell(sourceColumns.myMixColorId),
          sanitizeSpreadsheetCell(errorColumnLabel),
        ],
        ...rows.map(row => {
          const exportRow = toExportRow(row, resolveErrorMessage);

          return [
            sanitizeSpreadsheetCell(exportRow.skuId),
            sanitizeSpreadsheetCell(exportRow.quantity),
            sanitizeSpreadsheetCell(exportRow.description),
            sanitizeSpreadsheetCell(exportRow.myMixColorId),
            sanitizeSpreadsheetCell(exportRow.error),
          ];
        }),
      ];
    };

    const invalidSheet = XLSX.utils.aoa_to_sheet(buildSheetData(invalidRows));
    const allRowsSheet = XLSX.utils.aoa_to_sheet(buildSheetData(allRows));

    XLSX.utils.book_append_sheet(workbook, invalidSheet, unprocessedSheetName);
    XLSX.utils.book_append_sheet(workbook, allRowsSheet, allRowsSheetName);

    const workbookData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([workbookData], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const objectUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');

    downloadLink.href = objectUrl;
    downloadLink.download = getResultFileName(fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        type: 'Order import result export',
      },
    });
    throw error;
  }
};
